import { assertProjectDefinitionReferences } from '@/schemas/chip';
import type { ClipboardData } from '@/models/clipboard/ClipboardData';
import { createTemplateStampAction } from '@/services/ProjectTemplateService';
import { diagnostics, performanceDiagnostics } from '@/diagnostics/browserDiagnostics';
import { readSharedProjectFile } from '@/projectSharing/format';
import {
  canEditPlayerChip,
  assertPlayerDefinitionCapacity,
} from '@/challenges/playerChipPolicy';
import { projectRenameError } from '@/utils/projectName';
import type { ChipModelDto } from '@/schemas/chip';
import {
  createProjectLoadReporter,
  type ProjectLoadObserver,
  type ProjectLoadReport,
} from '@/utils/projectLoadProgress';
import { authorityFor, authorities, orderedEdit } from '@/simulation/native/authority';
import { projectStorageErrorMessage } from '@/utils/projectStorageErrorMessage';
import { ChipInstanceModel } from './ChipInstanceModel';
import { RuntimeGraphIds } from './logic/RuntimeGraphIds';
import { rekeyBundleAdapterSignals } from '@/utils/rekeyBundleAdapterSignals';
import { bundleAdapterValidation } from '@/utils/bundleAdapterValidation';
import { Point } from '@/models/Point';
import { cloneChipDraft, cloneChipDraftSteps } from '@/utils/cloneChipDraft';
import {
  planChipReplacement,
  applyChipReplacement,
  applyChipTintUpdates,
} from '@/models/ChipReplacement';
import {
  makeAutoObservable,
  autorun,
  computed,
  runInAction,
  reaction,
  untracked,
  observable,
  Reaction,
  type IComputedValue,
} from 'mobx';
import React from 'react';
import { SoundFeedback, type SoundCue } from '@/services/SoundFeedback';

import type {
  ChipDependencyInfo,
  IAppModel,
  IChipModel,
  IChipStoreModel,
  IPixiManagerModel,
  IUiModel,
  IUserPreferencesModel,
} from '@/interfaces/models';
import Context from '@/context';
import {
  ChipModelFactory,
  EditorModelFactory,
  EventBusFactory,
  ProjectModelFactory,
} from '@/factories';
import { generateTileImages } from '@/models/tiles/TileRegistry';
import { LogicGraph } from '@/models/logic/LogicGraph';
import { recoverSavedLatches } from '@/models/logic/recoverSavedLatches';
import { CircuitType } from '@/schemas/common';
import { EditingMode } from '@/models/EditorModel';
import type { ProjectDto, ChipExportBundle } from '@/schemas/chip';
import type { ChipId } from '@/types/ids';
import { CapturedProjectSnapshot } from '@/services/projectSnapshot/ProjectSnapshot';
import { encodeCurrentChip } from '@/services/projectSnapshot/CurrentChipFormat';
import { importChipFileBundle } from '@/services/importChipFile';
import { encodeProjectSnapshot } from '@/services/projectSnapshot/CurrentProjectFormat';
import type {
  CurrentPersistenceService,
  LoadedCurrentProject,
} from '@/services/CurrentPersistenceService';
import type { PersistenceService } from '@/services/PersistenceService';
import { topologicalSortChipDtos } from '@/utils/chipDtoSort';
import {
  observeProjectAuthoring,
  trackProjectAuthoring,
} from './observeProjectAuthoring';
import { hasNativeProjectSnapshotHooks } from './nativeProjectSnapshotHooks';
import { ViewStateStore } from '@/models/grid/ViewStateStore';
import { LogicCircuitModel } from '@/models/LogicCircuitModel';
import { validateSimulationCheckpoint } from '@/models/logic/SimulationCheckpoint';
import {
  authoredFingerprint,
  authoredFingerprintFromProject,
  captureContinuationMetadata,
  nativeCheckpointOptIn,
  readContinuationMetadata,
  restoredContinuationMetadata,
  restoreContinuationSources,
  type NativeContinuationMetadata,
} from '@/simulation/native/continuationMetadata';

const AUTO_SAVE_DELAY_MS = 1000;

export class ProjectNotFoundError extends Error {
  constructor(name: string) {
    super(`Project not found: ${name}`);
    this.name = 'ProjectNotFoundError';
  }
}

export class AppModel implements IAppModel {
  context?: Context;
  projectSaveError = '';
  workerSimulationEnabled: boolean;
  private projectBaselineReady = false;
  private saveGeneration = 0;
  workerModeSwitchInProgress = false;
  bundleIdentitySwitchInProgress = false;
  // A completed mode transition must also cancel older pending transport commands.
  simulationModeRevision = 0;
  manualSaveDirty = false;
  manualSaveInProgress = false;
  manualSaveDeparting = false;
  projectRenameInProgress = false;
  manualSaveCompletedAt: number | undefined;
  private manualDirtyDisposer?: () => void;
  private customSnapshotReaction?: Reaction;
  private customSnapshotContext?: Context;
  private customSnapshotInvalidated = false;
  private manualEditRevision = 0;
  private manualSavePromise?: Promise<void>;
  private explicitSavePromise?: Promise<void>;
  private saveIsExplicit = false;
  private pendingProjectSaves = new Set<Promise<void>>();
  chipDrafts = new Map<ChipId, { source: IChipModel; draft: IChipModel }>();
  private draftDirtyComputed = new Map<ChipId, IComputedValue<boolean>>();
  private loadGeneration = 0;
  private readonly openedStorageNames = new WeakMap<Context, string>();
  private soundFeedback: SoundFeedback;
  private autoSaveDisposer?: () => void;
  private debugModeDisposer?: () => void;
  private autoSaveTimer?: ReturnType<typeof setTimeout>;
  private graphicsInitialization?: Promise<void>;

  constructor(
    public uiModel: IUiModel,
    public chipStoreModel: IChipStoreModel,
    public pixiManagerModel: IPixiManagerModel,
    public userPreferencesModel: IUserPreferencesModel,
    private readonly chipModelFactory: ChipModelFactory,
    private readonly editorModelFactory: EditorModelFactory,
    private readonly projectModelFactory: ProjectModelFactory,
    private readonly eventBusFactory: EventBusFactory,
    private readonly persistenceService: Pick<
      PersistenceService,
      keyof PersistenceService
    > &
      Partial<
        Pick<
          CurrentPersistenceService,
          | 'loadProjectForEditing'
          | 'readProjectIdentity'
          | 'readProjectLastOpened'
          | 'recordProjectOpened'
          | 'watchProjectOpenHistory'
          | 'forgetProjectOpened'
        >
      >,
    options: { deferGraphics?: boolean } = {},
  ) {
    this.workerSimulationEnabled = userPreferencesModel.workerSimulationEnabled === true;
    this.soundFeedback = new SoundFeedback(() => this.userPreferencesModel.soundConfig);
    makeAutoObservable<
      this,
      | 'debugModeDisposer'
      | 'autoSaveDisposer'
      | 'autoSaveTimer'
      | 'soundFeedback'
      | 'loadProjectSteps'
      | 'loadGeneration'
      | 'openedStorageNames'
      | 'consumeDraftSteps'
      | 'draftDirtyComputed'
      | 'computeUnsavedChipDraft'
      | 'captureProjectForSave'
      | 'manualDirtyDisposer'
      | 'customSnapshotReaction'
      | 'customSnapshotContext'
      | 'customSnapshotInvalidated'
      | 'manualEditRevision'
      | 'manualSavePromise'
      | 'pendingProjectSaves'
      | 'projectBaselineReady'
      | 'saveGeneration'
      | 'explicitSavePromise'
      | 'saveIsExplicit'
      | 'graphicsInitialization'
    >(
      this,
      {
        soundFeedback: false,
        loadProjectSteps: false,
        loadGeneration: false,
        openedStorageNames: false,
        projectLoadGeneration: false,
        getEditingChipAsync: false,
        consumeDraftSteps: false,
        draftDirtyComputed: false,
        hasUnsavedChipDraft: false,
        computeUnsavedChipDraft: false,
        captureProjectForSave: false,
        autoSaveDisposer: false,
        debugModeDisposer: false,
        autoSaveTimer: false,
        manualDirtyDisposer: false,
        customSnapshotReaction: false,
        customSnapshotContext: false,
        customSnapshotInvalidated: false,
        manualEditRevision: false,
        manualSavePromise: false,
        pendingProjectSaves: false,
        projectBaselineReady: false,
        saveGeneration: false,
        explicitSavePromise: false,
        saveIsExplicit: false,
        graphicsInitialization: false,
      },
      { autoBind: true },
    );

    // Keep every UI entry point and open debug panel in sync with preferences.
    this.debugModeDisposer = autorun(() => {
      this.uiModel.setDebugModeEnabled?.(
        this.userPreferencesModel.debugModeEnabled === true,
      );
    });

    if (!options.deferGraphics) void this.init();
  }

  disposeDebugModeSync() {
    this.debugModeDisposer?.();
    this.debugModeDisposer = undefined;
  }

  playSound(cue: SoundCue) {
    this.soundFeedback.play(cue);
  }

  init(): Promise<void> {
    return (this.graphicsInitialization ??= (async () => {
      await this.pixiManagerModel.load();
      await generateTileImages();
    })().catch((error) => {
      this.graphicsInitialization = undefined;
      throw error;
    }));
  }

  /** Worker opt-in compatibility; saving preferences are independent. */
  get manualSaveEnabled() {
    return this.workerSimulationEnabled;
  }
  get autosaveEnabled() {
    return this.userPreferencesModel.autosaveEnabled;
  }
  get projectSaveDirty() {
    return this.manualSaveDirty;
  }
  get projectSaveInProgress() {
    return this.manualSaveInProgress;
  }
  get projectSaveCompletedAt() {
    return this.manualSaveCompletedAt;
  }
  get projectSaveBlockedReason() {
    if (this.hasUnsavedChipDrafts)
      return 'Save or discard chip drafts before saving the project.';
    const chipEditor = this.context?.editor?.currentChipEditor;
    if (
      chipEditor?.logicCircuitEditor?.historyModel?.currentTransaction ||
      chipEditor?.structureCircuitEditor?.historyModel?.currentTransaction
    )
      return 'Finish the current edit to save the project.';
    if (this.hasHeldProjectButton) return 'Release held buttons to save the project.';
    return '';
  }
  private get hasHeldProjectButton() {
    return (
      this.context?.chipStore.chips.some((chip) => {
        if (
          chip.logicCircuit.circuitModel.tiles.some((tile) => {
            const state = tile.state as {
              inputMode?: string;
              toggleState?: number;
            } | null;
            return (
              (tile.tileType === 'Button' ||
                (tile.tileType === 'SingleInterface' && state?.inputMode === 'button')) &&
              state?.toggleState === 1
            );
          })
        )
          return true;
        // Nested inspection inputs can have no physical tile in the parent view.
        // Their explicit input revision is observable; node runtime itself is not.
        return [...chip.logicCircuit.chipInstances.values()].some((instance) => {
          instance.trackSnapshotDependencies?.();
          return [...instance.logicGraph.nodes.values()].some(
            (node) => node.type === 'BUTTON' && node.state.isOn === true,
          );
        });
      }) ?? false
    );
  }
  setAutosaveEnabled(enabled: boolean) {
    this.userPreferencesModel.setAutosaveEnabled(enabled);
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = undefined;
    if (enabled) this.scheduleProjectAutosave();
  }
  private setupAutoSave(
    stored: ProjectDto | null = null,
    baselinePrepared = false,
    saveOnStart = false,
  ) {
    const pendingCustomEdit =
      baselinePrepared &&
      this.customSnapshotContext === this.context &&
      this.customSnapshotInvalidated;
    this.projectSaveError = '';
    this.autoSaveDisposer?.();
    this.manualDirtyDisposer?.();
    if (!baselinePrepared || this.customSnapshotContext !== this.context) {
      this.customSnapshotReaction?.dispose();
      this.customSnapshotReaction = undefined;
      this.customSnapshotContext = undefined;
      this.customSnapshotInvalidated = false;
    }
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = undefined;
    this.manualSaveDeparting = false;
    this.manualSaveDirty = !baselinePrepared || saveOnStart || pendingCustomEdit;
    this.manualSaveCompletedAt = undefined;
    this.manualEditRevision++;
    this.projectBaselineReady = baselinePrepared;
    if (!baselinePrepared) {
      try {
        const snapshot = this.captureProjectForSave();
        if (snapshot) {
          this.persistenceService.beginEditingProject(snapshot, stored);
          this.projectBaselineReady = true;
        }
      } catch (error) {
        this.projectSaveError = error instanceof Error ? error.message : String(error);
      }
    } else if (!saveOnStart) {
      let native = false;
      try {
        native = hasNativeProjectSnapshotHooks(this.context!);
      } catch {
        /* Use the guarded custom capture. */
      }
      if (!native && !this.customSnapshotReaction && !authorityFor(this.context)) {
        try {
          this.captureProjectForSave();
        } catch (error) {
          this.projectSaveError = error instanceof Error ? error.message : String(error);
        }
      }
    }
    this.armManualDirtyTracking();
    // Only preference/blocker transitions rearm this reaction. Save failures
    // wait for a new edit or explicit retry rather than creating a write loop.
    this.autoSaveDisposer = reaction(
      () => [this.autosaveEnabled, this.projectSaveBlockedReason] as const,
      ([enabled, blocked]) => {
        if (enabled && !blocked) this.scheduleProjectAutosave();
      },
    );
    if (!this.projectSaveError) this.scheduleProjectAutosave();
  }
  private scheduleProjectAutosave() {
    if (
      !this.context ||
      !this.autosaveEnabled ||
      this.projectRenameInProgress ||
      !this.manualSaveDirty ||
      this.manualSaveDeparting ||
      this.workerModeSwitchInProgress ||
      this.bundleIdentitySwitchInProgress ||
      this.manualSaveInProgress ||
      this.projectSaveBlockedReason
    )
      return;
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    const generation = this.saveGeneration;
    this.autoSaveTimer = setTimeout(() => {
      this.autoSaveTimer = undefined;
      if (
        generation !== this.saveGeneration ||
        !this.autosaveEnabled ||
        this.projectRenameInProgress ||
        this.manualSaveDeparting ||
        this.workerModeSwitchInProgress ||
        this.bundleIdentitySwitchInProgress ||
        this.projectSaveBlockedReason
      )
        return;
      void this.queueProjectSave(false).catch(() => {});
    }, AUTO_SAVE_DELAY_MS);
  }
  get hasUnsavedChipDrafts() {
    return [...this.chipDrafts.keys()].some((id) => this.hasUnsavedChipDraft(id));
  }

  get hasPendingProjectEdits() {
    return (authorityFor(this.context)?.pendingEdits ?? 0) > 0;
  }

  get hasUnsavedProjectChanges() {
    return (
      !this.manualSaveDeparting &&
      (this.projectRenameInProgress ||
        this.workerModeSwitchInProgress ||
        this.bundleIdentitySwitchInProgress ||
        this.manualSaveDirty ||
        this.manualSaveInProgress ||
        this.hasUnsavedChipDrafts)
    );
  }

  async changeBundleChipIdentityMode(enabled: boolean) {
    if (this.projectRenameInProgress)
      throw Error('Wait for the project rename to finish.');
    if (
      this.bundleIdentitySwitchInProgress ||
      this.workerModeSwitchInProgress ||
      this.manualSaveInProgress
    )
      throw Error('Wait for the current save or mode change to finish.');
    const context = this.context;
    const owner = authorityFor(context);
    if (owner?.error)
      throw Error('Recover the worker checkpoint before changing bundle matching.');
    const change = () => {
      if (context !== this.context) throw Error('Project changed during mode change.');
      if (context) {
        this.stopManualNativeSimulations(context);
        context.nativeRuntimeCheckpoint = false;
        context.nativeContinuationStatus = undefined;
      }
      this.userPreferencesModel.setBundleChipIdentityEnabled(enabled);
      if (!context) return;
      this.markProjectDirty();
      // Build children before parents, including inactive and active drafts. Normal delayed
      // reactions subsequently observe these templates without reusing old routing.
      const visited = new Set<IChipModel>();
      const pending = [
        ...context.chipStore.chips,
        ...Array.from(this.chipDrafts.values(), ({ draft }) => draft),
        context.editor.currentChip,
      ].map((chip) => ({ chip, ready: false }));
      while (pending.length) {
        const { chip, ready } = pending.pop()!;
        if (ready) {
          chip.logicCircuit.rebuildGraph();
          chip.rebuildTemplateGraph();
          continue;
        }
        if (visited.has(chip)) continue;
        visited.add(chip);
        pending.push({ chip, ready: true });
        for (const instance of chip.logicCircuit.circuitModel.chips)
          if (instance.chip && !visited.has(instance.chip))
            pending.push({ chip: instance.chip, ready: false });
      }
    };
    this.simulationModeRevision++;
    this.bundleIdentitySwitchInProgress = true;
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = undefined;
    let succeeded = false;
    try {
      if (owner) await owner.edit(() => runInAction(change));
      else runInAction(change);
      succeeded = true;
    } finally {
      runInAction(() => {
        this.bundleIdentitySwitchInProgress = false;
        if (succeeded) this.scheduleProjectAutosave();
      });
    }
  }

  async changeWorkerSimulationMode(
    enabled: boolean,
    rustWasm = this.userPreferencesModel.rustWasmSimulationEnabled === true,
  ) {
    if (this.projectRenameInProgress)
      throw Error('Wait for the project rename to finish.');
    if (this.bundleIdentitySwitchInProgress || this.workerModeSwitchInProgress)
      throw Error('Wait for the current mode change to finish.');
    if (this.hasUnsavedChipDrafts)
      throw Error('Save or discard chip drafts before changing simulation mode.');
    if (this.hasHeldProjectButton)
      throw Error('Release held buttons before changing simulation mode.');
    const rustChanged =
      rustWasm !== (this.userPreferencesModel.rustWasmSimulationEnabled === true);
    if (enabled === this.workerSimulationEnabled && !rustChanged) return;
    if (rustChanged && enabled !== this.workerSimulationEnabled)
      throw Error('Enable Worker simulation before changing its Rust/WASM option.');
    const context = this.context;
    const previousMode = this.workerSimulationEnabled;
    const previousContinuation = context?.nativeRuntimeCheckpoint;
    let stopWatching: (() => void) | undefined;
    let pendingLateEdits = false;
    this.simulationModeRevision++;
    this.workerModeSwitchInProgress = true;
    this.autoSaveDisposer?.();
    this.autoSaveDisposer = undefined;
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = undefined;
    this.manualDirtyDisposer?.();
    this.manualDirtyDisposer = undefined;
    try {
      // A checkpoint may be queued before its storage promise becomes visible.
      // Drain the complete save operation before releasing the runtime.
      await (this.explicitSavePromise ?? this.manualSavePromise);
      if (this.context !== context) throw Error('Project changed during mode change.');
      if (this.projectSaveBlockedReason) throw Error(this.projectSaveBlockedReason);
      const owner = authorityFor(context);
      if (context) {
        if (owner?.error && owner.ownsRuntime)
          throw Error('Recover the worker checkpoint before changing simulation mode.');
        if (owner?.error) owner.dispose();
        else if (owner) await owner.edit(() => {});
        this.stopManualNativeSimulations(context);
        // A detached automatic save can still be writing. Finish it before
        // capturing the definitive paused snapshot for this transition.
        await Promise.all([...this.pendingProjectSaves]);
        if (this.context !== context) throw Error('Project changed during mode change.');
        runInAction(() => {
          context.nativeRuntimeCheckpoint = false;
        });
        const snapshot = this.captureProjectForSave();
        if (!snapshot) throw Error('Unable to capture this project.');
        const revision = this.manualEditRevision;
        let initial = true;
        let changed = false;
        stopWatching = autorun(() => {
          observeProjectAuthoring(context);
          if (initial) initial = false;
          else changed = true;
        });
        await this.persistProjectChanges(snapshot);
        if (
          this.context !== context ||
          changed ||
          revision !== this.manualEditRevision ||
          this.hasUnsavedChipDrafts
        ) {
          pendingLateEdits = this.context === context;
          throw Error('New changes arrived during Save. Review and save again.');
        }
      }
      runInAction(() => {
        if (rustChanged)
          this.userPreferencesModel.setRustWasmSimulationEnabled!(rustWasm);
        else this.userPreferencesModel.setWorkerSimulationEnabled(enabled);
        owner?.dispose();
        this.workerSimulationEnabled = enabled;
        if (context) {
          context.nativeContinuationStatus = undefined;
          this.setupAutoSave(null, true);
          if (enabled) this.manualSaveCompletedAt = Date.now();
        }
      });
    } catch (error) {
      runInAction(() => {
        if (context && this.context === context) {
          this.workerSimulationEnabled = previousMode;
          context.nativeRuntimeCheckpoint = previousContinuation;
          this.setupAutoSave(null, true, true);
          this.projectSaveError = error instanceof Error ? error.message : String(error);
        }
      });
      throw error;
    } finally {
      stopWatching?.();
      runInAction(() => {
        this.workerModeSwitchInProgress = false;
        // A failed serializer/storage operation waits for a new edit or Retry.
        // Edits that arrived after a successful cut still need their normal save.
        if (!this.projectSaveError || pendingLateEdits) this.scheduleProjectAutosave();
      });
    }
  }

  markProjectDirty() {
    if (!this.context || this.manualSaveDeparting) return;
    this.manualEditRevision++;
    this.manualSaveDirty = true;
    this.scheduleProjectAutosave();
  }
  markManualProjectDirty() {
    this.markProjectDirty();
  }
  private armManualDirtyTracking() {
    this.manualDirtyDisposer?.();
    const context = this.context;
    if (!context) return;
    this.manualDirtyDisposer = trackProjectAuthoring(context, () =>
      this.markProjectDirty(),
    );
  }

  private stopManualNativeSimulations(context: Context) {
    const chips = [
      ...context.chipStore.chips,
      ...Array.from(this.chipDrafts.values(), ({ draft }) => draft),
    ];
    for (const chip of chips) {
      const simulation = chip.logicCircuit.logicSimulation;
      if (!authorities.has(simulation)) simulation.stop();
    }
  }

  async prepareManualProjectLeave() {
    if (this.projectRenameInProgress)
      throw Error('Wait for the project rename to finish.');
    if (
      this.manualSaveInProgress ||
      this.workerModeSwitchInProgress ||
      this.bundleIdentitySwitchInProgress
    )
      throw Error('Wait for Save to finish before leaving.');
    const context = this.context;
    if (!context) return;
    const owner = authorityFor(context);
    // This barrier stops/releases the runtime; it never writes project storage.
    if (owner && !owner.error) await owner.closeForProject(async () => {});
    // Explicit Discard can abandon a failed owner without a capture/recovery.
    // Release ownership before native stop; this path never writes storage.
    owner?.dispose();
    this.stopManualNativeSimulations(context);
    runInAction(() => {
      this.manualSaveDeparting = true;
      this.manualDirtyDisposer?.();
      this.manualDirtyDisposer = undefined;
      this.customSnapshotReaction?.dispose();
      this.customSnapshotReaction = undefined;
      this.customSnapshotContext = undefined;
      this.customSnapshotInvalidated = false;
    });
  }

  createProject(name: string): Context {
    this.loadGeneration++;
    if (this.context || this.chipStoreModel.chips.length > 0) {
      this.teardown();
    }

    const context = this.createBaseContext();

    const mainChip = this.chipModelFactory.create(context, {
      name: 'Main',
      description: 'Main chip',
      color: 'hsl(210, 70%, 50%)',
    });
    context.mainChip = mainChip;
    mainChip.deferUnusedTemplate = true;

    this.chipStoreModel.addChip(mainChip);

    context.project = this.projectModelFactory.create(context, name);
    context.editor = this.editorModelFactory.create(context, mainChip);
    if (this.manualSaveEnabled) mainChip.logicCircuit.logicSimulation.stop();
    context.viewStateStore.setStorageKey(name);

    this.context = context;
    this.setupAutoSave();

    return context;
  }

  loadProject(projectDto: ProjectDto, storedBaseline: ProjectDto | null = null): Context {
    assertProjectDefinitionReferences(projectDto);
    const generation = ++this.loadGeneration;
    this.teardown();
    const timing = performanceDiagnostics.start('load');
    const steps = this.loadProjectSteps(projectDto, storedBaseline);
    try {
      let result = steps.next();
      while (!result.done) result = steps.next();
      return result.value;
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError'))
        diagnostics.record('load');
      steps.return(undefined as never);
      if (generation === this.loadGeneration) this.teardown();
      throw error;
    } finally {
      performanceDiagnostics.finish('load', timing);
    }
  }

  private async loadProjectCooperatively(
    projectDto: ProjectDto,
    signal: AbortSignal | undefined,
    generation: number,
    beginEditing?: LoadedCurrentProject['beginEditing'],
    report?: ProjectLoadReport,
  ): Promise<Context> {
    const timing = performanceDiagnostics.start('load');
    const steps = this.loadProjectSteps(
      projectDto,
      beginEditing ? null : projectDto,
      beginEditing,
      report,
    );
    let started = performance.now();
    try {
      while (true) {
        if (signal?.aborted || generation !== this.loadGeneration)
          throw new DOMException('Project load cancelled', 'AbortError');
        const result = runInAction(() => steps.next());
        if (signal?.aborted || generation !== this.loadGeneration)
          throw new DOMException('Project load cancelled', 'AbortError');
        if (result.done) return result.value;
        if (signal && performance.now() - started >= 8) {
          // Yield a task so loading can paint and navigation can cancel this load.
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
          started = performance.now();
        }
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError'))
        diagnostics.record('load');
      steps.return(undefined as never);
      if (generation === this.loadGeneration) this.teardown();
      throw error;
    } finally {
      performanceDiagnostics.finish('load', timing);
    }
  }

  private *loadProjectSteps(
    projectDto: ProjectDto,
    storedBaseline: ProjectDto | null,
    beginEditing?: LoadedCurrentProject['beginEditing'],
    report?: ProjectLoadReport,
  ): Generator<void, Context> {
    const context = this.createBaseContext();
    context.startPaused = projectDto.startPaused === true;
    if (context.startPaused) context.nativeRuntimeCheckpoint = false;
    let continuation: NativeContinuationMetadata | undefined;
    let resumedLogic: LogicCircuitModel | undefined;
    const rootDto = projectDto.chips.find((chip) => chip.id === projectDto.mainChipId);
    if (context.nativeRuntimeCheckpoint && rootDto?.nativeContinuation !== undefined) {
      try {
        continuation = readContinuationMetadata(rootDto.nativeContinuation);
        if (
          projectDto.chips.some((chip) =>
            chip.chipInstanceDtos.some(
              (instance) => instance.chipId === projectDto.mainChipId,
            ),
          )
        )
          throw Error('Continuation root must not be embedded in another definition');
        if (
          continuation.authoredFingerprint !==
          authoredFingerprintFromProject(projectDto, rootDto)
        )
          throw Error('Continuation authored topology/dependency mismatch');
        context.nativeContinuationHydratingChipId = projectDto.mainChipId;
      } catch (error) {
        continuation = undefined;
        context.nativeContinuationStatus = `Native fallback: ${String(error)}`;
      }
    }

    let published = false;
    try {
      context.isHydrating = true;
      const sorted = topologicalSortChipDtos(projectDto.chips);

      const totalTiles = sorted.reduce(
        (count, chip) =>
          count +
          chip.logicCircuitDto.tileManagerDto.tiles.length +
          chip.structureCircuitDto.tileManagerDto.tiles.length,
        0,
      );
      let hydratedTiles = 0;
      report?.('hydrating', 0, totalTiles);
      let mainChip: IChipModel | undefined;
      for (const chipDto of sorted) {
        const chip = yield* this.chipModelFactory.fromDtoSteps(
          context,
          chipDto,
          report &&
            ((completed) => report('hydrating', hydratedTiles + completed, totalTiles)),
        );
        hydratedTiles +=
          chipDto.logicCircuitDto.tileManagerDto.tiles.length +
          chipDto.structureCircuitDto.tileManagerDto.tiles.length;
        this.chipStoreModel.addChip(chip);
        if (chipDto.id === projectDto.mainChipId) {
          mainChip = chip;
        }
        yield;
      }

      report?.('hydrating', totalTiles, totalTiles);
      if (!mainChip) {
        throw new Error(`Main chip not found: ${projectDto.mainChipId}`);
      }

      context.mainChip = mainChip;
      context.project = this.projectModelFactory.create(context, projectDto.name);
      context.editor = this.editorModelFactory.create(context, mainChip);
      context.viewStateStore.setStorageKey(projectDto.name);

      // Older saves could cache a placed-instance DTO while the parent simulation
      // continued. Its editing graph is authoritative for nodes shared by ID.
      for (const chip of this.chipStoreModel.chips) {
        const savedGraph = chip.editingInstance.logicGraph;
        const savedByExternalId = new Map(
          [...savedGraph.nodes.values()].map((node) => [node.idText, node]),
        );
        for (const instance of chip.logicCircuit.circuitModel.chips) {
          for (const node of instance.logicGraph.nodes.values()) {
            const saved = savedByExternalId.get(node.idText);
            if (saved) node.restoreRuntimeState(saved);
          }
        }
      }

      // Only an unreferenced root can omit its independent clone. Authored child
      // instances (not the root editing instance) require eager dependency templates.
      mainChip.deferUnusedTemplate = !projectDto.chips.some((owner) =>
        owner.chipInstanceDtos.some((instance) => instance.chipId === mainChip!.id),
      );

      // Build child templates before their parents, but keep saved instance graphs:
      // embedding restores runtime by stable node identity after cloning a template.
      let completedTemplates = 0;
      report?.('templates', 0, sorted.length);
      for (const chip of this.chipStoreModel.chips) {
        if (!chip.deferUnusedTemplate) {
          if (chip.rebuildTemplateGraphSteps) yield* chip.rebuildTemplateGraphSteps();
          else chip.rebuildTemplateGraph();
        }
        report?.('templates', ++completedTemplates, sorted.length);
        yield;
      }

      // Pre-seed each chip's GraphManager with the editing instance's serialized
      // logic graph so that rebuildGraph() can save/restore node state (e.g. SR latch
      // feedback state). Tiles already have their serialized logicNodeIds from the DTO,
      // which map to node IDs in this graph.
      for (const chip of this.chipStoreModel.chips) {
        const editingGraph = chip.editingInstance.logicGraph;
        if (editingGraph && editingGraph.nodes.size > 0) {
          chip.logicCircuit.graphManager.graph = editingGraph as LogicGraph;
        }
      }

      let completedGraphs = 0;
      report?.('graphs', 0, sorted.length);
      for (const chip of this.chipStoreModel.chips) {
        if (continuation && chip.id === projectDto.mainChipId) {
          const logic = chip.logicCircuit as LogicCircuitModel;
          yield* logic.rebuildGraphForContinuationSteps();
          try {
            if (authoredFingerprint(context, chip) !== continuation.authoredFingerprint)
              throw Error('Hydrated continuation authoring bindings differ');
            logic.adoptContinuationGraph(continuation.simulation);
            validateSimulationCheckpoint(continuation.simulation, logic.logicGraph);
          } catch (error) {
            continuation = undefined;
            context.nativeContinuationHydratingChipId = undefined;
            context.nativeContinuationStatus = `Native fallback: ${String(error)}`;
            logic.logicSimulation.isRunning = true;
          }
          if (continuation) {
            // No target-owner node is processed during reconstruction. Restore
            // raw state, timing/frontier and intent only after binding validation.
            // Scheduling controls are shared preferences; align their source
            // before deferred constructor reactions can overwrite this runtime.
            context.userPreferences.setSimulationConfig({
              maximumSpeed: continuation.simulation.maximumSpeed,
              maxSyncIterations: continuation.simulation.maxSyncIterations,
              asyncDelay: continuation.simulation.asyncDelay,
            });
            logic.logicSimulation.restoreContinuation(continuation.simulation, false);
            restoreContinuationSources(chip, continuation);
            logic.syncStateToTiles();
            restoredContinuationMetadata.set(logic, continuation);
            resumedLogic = logic;
            context.nativeContinuationStatus = 'Exact native continuation restored';
            report?.('graphs', ++completedGraphs, sorted.length);
            yield;
            continue;
          }
        }
        if (chip.logicCircuit.rebuildGraphSteps && chip.rebuildTemplateGraphSteps) {
          yield* chip.logicCircuit.rebuildGraphSteps();
          if (!chip.deferUnusedTemplate) yield* chip.rebuildTemplateGraphSteps();
        } else chip.logicCircuit.rebuildGraph();
        if (!context.startPaused) this.recoverRestoredLatches(chip);
        if (this.manualSaveEnabled || context.startPaused)
          chip.logicCircuit.logicSimulation.stop();
        report?.('graphs', ++completedGraphs, sorted.length);
        yield;
      }

      report?.('preparing', 0, 1);
      let initialSnapshot: ProjectDto | CapturedProjectSnapshot;
      if (resumedLogic && continuation) {
        // The override lives for this synchronous capture only, never a yield.
        captureContinuationMetadata.set(resumedLogic, continuation);
        try {
          initialSnapshot = this.persistenceService.currentSnapshotsEnabled
            ? this.captureProjectForSave(context)!
            : context.project.dto;
        } finally {
          captureContinuationMetadata.delete(resumedLogic);
        }
      } else {
        initialSnapshot = this.persistenceService.currentSnapshotsEnabled
          ? this.captureProjectForSave(context)!
          : context.project.dtoSteps
            ? yield* context.project.dtoSteps()
            : context.project.dto;
      }
      yield;
      if (beginEditing) beginEditing(initialSnapshot);
      else
        yield* this.persistenceService.beginEditingProjectSteps(
          initialSnapshot,
          storedBaseline,
        );
      context.isHydrating = false;
      context.nativeContinuationHydratingChipId = undefined;
      this.context = context;
      this.setupAutoSave(storedBaseline, true);
      published = true;
      resumedLogic?.logicSimulation.activateContinuation();
      report?.('preparing', 1, 1);
      return context;
    } finally {
      if (!published) context.runtimeGraphIds?.close();
    }
  }

  private assertChallengeLibraryEditable(chip?: IChipModel) {
    if (this.context?.challenge && (!chip || !canEditPlayerChip(this.context, chip)))
      throw Error('The author chip library is read-only.');
  }

  private assertChallengeCreation(kind?: 'bundle-adapter') {
    const context = this.context;
    if (!context?.challenge || context.challengeAuthoring) return;
    const policy = context.challenge.playerChips;
    if (!policy) throw Error('The challenge chip library is read-only.');
    if (
      !policy.create ||
      !(kind === 'bundle-adapter' ? policy.bundleAdapters : policy.chips)
    )
      throw Error('Creating this chip category is disabled.');
    assertPlayerDefinitionCapacity(context);
  }

  createChip(
    name: string = 'New Chip',
    description: string = '',
    color: string = 'hsl(0, 0%, 50%)',
    kind?: 'bundle-adapter',
  ) {
    this.assertChallengeCreation(kind);
    if (!this.context) {
      throw new Error('Context not initialized. Create a project first.');
    }

    const newChip = this.chipModelFactory.create(this.context, {
      name,
      description,
      color,
      kind,
    });

    this.chipStoreModel.addChip(newChip);

    return newChip;
  }

  createTemplate(
    name: string,
    description = '',
    color = 'hsl(0, 0%, 50%)',
    templateType: 'logic' | 'structure' = 'logic',
    data?: ClipboardData,
  ): IChipModel {
    const context = this.context;
    if (!context) throw new Error('Create a project first.');
    if (context.challenge) throw new Error('Templates are available in project editors.');
    const template = this.chipModelFactory.create(context, {
      name,
      description,
      color,
      templateType,
    });
    this.chipStoreModel.addChip(template);
    if (data) {
      if (data.origin) template.templateOrigin = { ...data.origin };
      // Capture into an isolated editor without navigating or borrowing source identities.
      const chipEditor = this.chipStoreModel.getChipEditor(template.id)!;
      const circuitEditor = chipEditor.currentCircuitEditor;
      const action = createTemplateStampAction(
        context,
        { ...data, origin: undefined },
        { x: 0, y: 0 },
        circuitEditor.circuitModel,
        chipEditor,
        templateType,
      );
      if (!action) {
        this.chipStoreModel.deleteChip(template.id);
        throw new Error(
          'This selection cannot be saved as a template. Select complete chips and valid dependencies.',
        );
      }
      action.do();
      template.logicCircuit.rebuildGraph();
    }
    return template;
  }

  duplicateChip(sourceChip: IChipModel) {
    this.assertChallengeCreation(sourceChip.kind);
    if (!this.context) {
      throw new Error('Context not initialized. Create a project first.');
    }

    const dto = cloneChipDraft(sourceChip.dto);
    rekeyBundleAdapterSignals(dto);
    const duplicated = this.chipModelFactory.fromDto(this.context, dto);
    this.chipStoreModel.addChip(duplicated);
    duplicated.rebuildTemplateGraph();

    return duplicated;
  }

  /** Restore an autosaved authoring draft without publishing it to placed instances. */
  restoreChipDraft(sourceId: string, dto: ChipModelDto) {
    this.assertChallengeLibraryEditable();
    const source = this.chipStoreModel.chips.find((chip) => chip.id === sourceId);
    if (!this.context || !source || !this.chipStoreModel.addDraft || dto.id === source.id)
      throw Error('The saved chip draft has no matching source.');
    const draft = this.chipModelFactory.fromDto(this.context, dto);
    draft.logicCircuit.recursionOwnerId = source.id;
    draft.deferUnusedTemplate = draft.kind !== 'bundle-adapter';
    this.chipStoreModel.addDraft(draft);
    this.chipDrafts.set(draft.id, { source, draft });
    draft.logicCircuit.rebuildGraph();
    return draft;
  }

  getSourceChip(chip: IChipModel): IChipModel {
    return this.chipDrafts.get(chip.id)?.source ?? chip;
  }

  getEditingChip(chip: IChipModel): IChipModel {
    if (this.context?.challenge && chip.id !== this.context.mainChip.id)
      this.assertChallengeLibraryEditable(chip);
    if (
      !this.context ||
      !this.chipStoreModel.addDraft ||
      chip.id === this.context.mainChip.id ||
      this.chipDrafts.has(chip.id)
    )
      return chip;
    const existing = [...this.chipDrafts.values()].find(
      (entry) => entry.source.id === chip.id,
    );
    if (existing) return existing.draft;
    const draft = this.chipModelFactory.fromDto(this.context, cloneChipDraft(chip.dto));
    draft.logicCircuit.recursionOwnerId = chip.id;
    // Drafts are not library definitions and have no placed instances. Their
    // live graph is enough for editing; publishing builds the saved template.
    draft.deferUnusedTemplate = draft.kind !== 'bundle-adapter';
    this.chipStoreModel.addDraft(draft);
    this.chipDrafts.set(draft.id, { source: chip, draft });
    draft.logicCircuit.rebuildGraph();
    this.recoverRestoredLatches(draft);
    if (this.manualSaveEnabled) draft.logicCircuit.logicSimulation.stop();
    return draft;
  }

  private async consumeDraftSteps<T>(
    steps: Generator<void, T>,
    isCurrent: () => boolean,
  ): Promise<T> {
    let started = performance.now();
    let finished = false;
    try {
      while (true) {
        if (!isCurrent())
          throw new DOMException('Chip navigation cancelled', 'AbortError');
        const result = runInAction(() => steps.next());
        if (result.done) {
          finished = true;
          return result.value;
        }
        if (performance.now() - started >= 8) {
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
          started = performance.now();
        }
      }
    } finally {
      if (!finished) runInAction(() => steps.return(undefined as never));
    }
  }

  async getEditingChipAsync(
    chip: IChipModel,
    isCurrent: () => boolean,
  ): Promise<IChipModel> {
    if (this.context?.challenge && chip.id !== this.context.mainChip.id)
      this.assertChallengeLibraryEditable(chip);
    if (
      !this.context ||
      !this.chipStoreModel.addDraft ||
      chip.id === this.context.mainChip.id
    )
      return chip;
    const existing = [...this.chipDrafts.values()].find(
      (entry) => entry.source.id === chip.id,
    );
    if (existing) return existing.draft;
    const context = this.context;
    const generation = this.loadGeneration;
    const valid = () =>
      isCurrent() && this.context === context && generation === this.loadGeneration;
    // Allow the click to paint its loading state before serializing the source.
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
    if (!valid()) throw new DOMException('Chip navigation cancelled', 'AbortError');
    const dto = await this.consumeDraftSteps(cloneChipDraftSteps(chip.dto), valid);
    const draft = await this.consumeDraftSteps(
      this.chipModelFactory.fromDtoSteps(context, dto),
      valid,
    );
    draft.deferUnusedTemplate = draft.kind !== 'bundle-adapter';
    try {
      if (draft.logicCircuit.rebuildGraphSteps && draft.rebuildTemplateGraphSteps) {
        await this.consumeDraftSteps(draft.logicCircuit.rebuildGraphSteps(), valid);
        if (!draft.deferUnusedTemplate)
          await this.consumeDraftSteps(draft.rebuildTemplateGraphSteps(), valid);
      } else draft.logicCircuit.rebuildGraph();
      if (!valid()) throw new DOMException('Chip navigation cancelled', 'AbortError');
      runInAction(() => {
        this.recoverRestoredLatches(draft);
        if (this.manualSaveEnabled) draft.logicCircuit.logicSimulation.stop();
        this.chipStoreModel.addDraft!(draft);
        this.chipDrafts.set(draft.id, { source: chip, draft });
      });
      return draft;
    } catch (error) {
      draft.logicCircuit.dispose?.();
      draft.logicCircuit.circuitModel.connectionModel.dispose();
      draft.structureCircuit.circuitModel.connectionModel.dispose();
      throw error;
    }
  }

  hasUnsavedChipDraft(draftId: ChipId): boolean {
    if (!this.chipDrafts.has(draftId)) return false;
    let cached = this.draftDirtyComputed.get(draftId);
    if (!cached) {
      cached = computed(() => this.computeUnsavedChipDraft(draftId), { keepAlive: true });
      this.draftDirtyComputed.set(draftId, cached);
    }
    return cached.get();
  }

  private computeUnsavedChipDraft(draftId: ChipId): boolean {
    const entry = this.chipDrafts.get(draftId);
    if (!entry) return false;
    const editor = this.chipStoreModel.getChipEditor(draftId);
    const changedHistory = [
      editor?.logicCircuitEditor,
      editor?.structureCircuitEditor,
    ].some(
      (circuit) =>
        circuit &&
        (circuit.historyModel.previousTransactions.length > 0 ||
          (circuit.historyModel.currentTransaction?.actions.length ?? 0) > 0),
    );
    if (changedHistory) return true;
    const { source, draft } = entry;
    const tileAppearance = (chip: IChipModel) =>
      [chip.logicCircuit, chip.structureCircuit].map((circuit) =>
        circuit.circuitModel.tiles.map((tile) => [
          tile.id,
          tile.location.x,
          tile.location.y,
          tile.meta.tint,
        ]),
      );
    return (
      source.name !== draft.name ||
      source.description !== draft.description ||
      source.color !== draft.color ||
      source.combinedColorDisplay !== draft.combinedColorDisplay ||
      source.folder !== draft.folder ||
      JSON.stringify(source.templateOrigin) !== JSON.stringify(draft.templateOrigin) ||
      source.structureCircuit.originLocation.x !==
        draft.structureCircuit.originLocation.x ||
      source.structureCircuit.originLocation.y !==
        draft.structureCircuit.originLocation.y ||
      JSON.stringify(source.interfaceLinks.map((link) => link.dto)) !==
        JSON.stringify(draft.interfaceLinks.map((link) => link.dto)) ||
      JSON.stringify(tileAppearance(source)) !== JSON.stringify(tileAppearance(draft))
    );
  }

  updateChipDetails(
    chip: IChipModel,
    details: Pick<IChipModel, 'name' | 'description' | 'color' | 'combinedColorDisplay'>,
  ) {
    this.assertChallengeLibraryEditable(chip);
    return orderedEdit(this.context!, () => {
      const source = this.getSourceChip(chip);
      Object.assign(source, details);
      for (const entry of this.chipDrafts.values()) {
        if (entry.source.id === source.id) Object.assign(entry.draft, details);
      }
    });
  }

  discardChipDraft(draftId: ChipId) {
    if (
      this.context &&
      [...this.context.chipStore.chips, ...[this.context.editor.currentChip]].some(
        (chip) => authorities.has(chip.logicCircuit),
      )
    )
      throw Error('Release worker before discarding a draft');
    const entry = this.chipDrafts.get(draftId);
    if (!entry || !this.context) return;
    if (this.context.editor.currentChip.id === draftId)
      this.context.editor.currentChip = this.context.mainChip;
    this.chipStoreModel.deleteChip(draftId);
    for (const instance of entry.draft.logicCircuit.chipInstances.values())
      this.context.project.chipInstanceMap.delete(instance.id);
    this.chipDrafts.delete(draftId);
    this.draftDirtyComputed.delete(draftId);
  }

  saveChipDraft(
    draftId: ChipId,
    mode: 'update' | 'copy',
    keepEditing = true,
  ): IChipModel {
    const editing = this.chipDrafts.get(draftId)?.source;
    this.assertChallengeLibraryEditable(editing);
    if (mode === 'copy') this.assertChallengeCreation(editing?.kind);
    if (this.context && authorities.has(this.context.editor.currentChip.logicCircuit))
      throw Error('Release worker before replacing a chip draft');
    const entry = this.chipDrafts.get(draftId);
    if (!entry || !this.context) throw new Error('No chip draft is open.');
    const { source, draft } = entry;
    const adapterProblem = bundleAdapterValidation(draft);
    if (adapterProblem) throw new Error(adapterProblem);
    if (
      draft.kind === 'bundle-adapter' &&
      !source.structureCircuit.circuitModel.tileManagerModel.tiles.length
    ) {
      const tiles = draft.structureCircuit.circuitModel.tileManagerModel.tiles;
      draft.structureCircuit.originLocation = new Point(
        Math.min(...tiles.map((tile) => tile.location.x)),
        Math.min(...tiles.map((tile) => tile.location.y)),
      );
    }
    const owners = [
      ...this.chipStoreModel.chips,
      ...[...this.chipDrafts.values()]
        .filter((item) => item !== entry)
        .map((item) => item.draft),
    ];
    const plans =
      mode === 'update' ? planChipReplacement(this.context, source, draft, owners) : [];
    if (
      mode === 'update' &&
      !this.userPreferencesModel.recursiveChipsEnabled &&
      draft.recursiveChildChips.some((child) => child.id === source.id)
    )
      throw new Error(
        'A chip cannot contain itself. Remove the circular dependency or save a new copy.',
      );
    const dto = cloneChipDraft(draft.dto);
    dto.id = mode === 'update' ? source.id : dto.id;
    dto.editingInstanceDto.chipId = dto.id;
    if (mode === 'copy') {
      dto.name = `${draft.name} - Copy`;
      rekeyBundleAdapterSignals(dto);
    }
    let saved = this.chipModelFactory.fromDto(this.context, dto);
    if (mode === 'update') {
      if (!plans.length) applyChipTintUpdates(source, draft, owners);
      source.logicCircuit.dispose?.();
      source.logicCircuit.circuitModel.connectionModel.dispose();
      source.structureCircuit.circuitModel.connectionModel.dispose();
      source.name = saved.name;
      source.description = saved.description;
      source.color = saved.color;
      source.combinedColorDisplay = saved.combinedColorDisplay;
      source.kind = saved.kind;
      source.templateType = saved.templateType;
      source.templateOrigin = saved.templateOrigin
        ? { ...saved.templateOrigin }
        : undefined;
      source.adapterSignals = { ...saved.adapterSignals };
      source.logicCircuit = saved.logicCircuit;
      source.structureCircuit = saved.structureCircuit;
      source.editingInstance = saved.editingInstance;
      source.interfaceLinkMap = saved.interfaceLinkMap;
      const editingCircuit = source.logicCircuit;
      editingCircuit.onGraphReady = (graph) => {
        if (source.logicCircuit === editingCircuit)
          ChipInstanceModel.adoptEditingGraph(source.editingInstance, graph);
      };
      source.logicCircuit.onAfterRebuild = () => {
        if (!source.deferUnusedTemplate) source.rebuildTemplateGraph();
      };
      source.templateGraphVersion += 1;
      saved = source;
      applyChipReplacement(draft, plans);
    }
    this.chipStoreModel.addChip(saved);
    saved.logicCircuit.rebuildGraph();
    for (const owner of new Set(plans.map((plan) => plan.owner))) {
      const history = this.chipStoreModel.getChipEditor(owner.id)?.logicCircuitEditor
        .historyModel;
      if (history) {
        history.previousTransactions = [];
        history.futureTransactions = [];
        history.currentTransaction = null;
      }
      owner.logicCircuit.rebuildGraph();
    }
    if (this.userPreferencesModel.recursiveChipsEnabled) {
      // Replacement can close a cycle in an existing owner. Re-evaluate every
      // member after the saved definition is installed, regardless of ID order.
      for (const chip of this.chipStoreModel.chips) {
        if (
          chip !== saved &&
          chip.recursiveChildChips.some((child) => child.id === chip.id)
        )
          chip.logicCircuit.rebuildGraph();
      }
    }
    if (
      this.context.editor.placeChipModel.selectedChip?.id === source.id &&
      mode === 'update'
    )
      this.context.editor.placeChipModel.selectedChip = saved;
    if (keepEditing && this.context.editor.currentChip.id === draftId) {
      // Keep the canvas, selection and viewport alive; this working copy now
      // starts from the definition we just published.
      entry.source = saved;
      draft.name = saved.name;
      draft.folder = saved.folder;
      draft.adapterSignals = { ...saved.adapterSignals };
      if (draft.kind === 'bundle-adapter') draft.rebuildTemplateGraph();
      draft.logicCircuit.recursionOwnerId = saved.id;
      const editor = this.chipStoreModel.getChipEditor(draftId);
      for (const circuit of [
        editor?.logicCircuitEditor,
        editor?.structureCircuitEditor,
      ]) {
        if (!circuit) continue;
        circuit.historyModel.previousTransactions = [];
        circuit.historyModel.futureTransactions = [];
        circuit.historyModel.currentTransaction = null;
      }
      if (!this.context.editor.openChipIds.includes(saved.id))
        this.context.editor.openChipIds.push(saved.id);
    } else {
      this.discardChipDraft(draftId);
    }
    return saved;
  }

  /**
   * Navigate to a chip in the editor (switch to it and set to logic editing mode)
   */
  navigateToChip(chip: IChipModel) {
    if (!this.context?.editor) {
      throw new Error('Editor not initialized');
    }

    this.context.editor.setCurrentChip(chip);

    const chipEditor = this.context.editor.currentChipEditor;
    if (chipEditor) {
      chipEditor.editingMode = CircuitType.ChipLogic;
    }
  }

  saveCurrentProject(): Promise<void> {
    if (this.projectRenameInProgress)
      return Promise.reject(Error('Wait for the project rename to finish.'));
    if (this.workerModeSwitchInProgress || this.bundleIdentitySwitchInProgress)
      return Promise.reject(Error('Wait for the simulation mode change to finish.'));
    if (this.projectSaveBlockedReason)
      return Promise.reject(Error(this.projectSaveBlockedReason));
    return this.queueProjectSave(true);
  }
  private queueProjectSave(explicit: boolean): Promise<void> {
    if (this.manualSavePromise) {
      if (!explicit || this.saveIsExplicit) return this.manualSavePromise;
      if (this.explicitSavePromise) return this.explicitSavePromise;
      const context = this.context,
        generation = this.saveGeneration;
      // A click during an automatic write saves again after that write, so edits
      // newer than its capture are included in the explicit request.
      const pending = this.manualSavePromise
        .catch(() => {})
        .then(() => {
          if (this.context !== context || this.saveGeneration !== generation)
            throw Error('Project changed before Save could start.');
          return this.queueProjectSave(true);
        });
      this.explicitSavePromise = pending;
      pending.then(
        () => {
          if (this.explicitSavePromise === pending) this.explicitSavePromise = undefined;
        },
        () => {
          if (this.explicitSavePromise === pending) this.explicitSavePromise = undefined;
        },
      );
      return pending;
    }
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = undefined;
    this.manualSaveInProgress = true;
    this.saveIsExplicit = explicit;
    const pending = this.performProjectSave(this.context, this.saveGeneration);
    this.manualSavePromise = pending;
    return pending;
  }
  private async performProjectSave(
    context: Context | undefined,
    generation: number,
  ): Promise<void> {
    const timing = performanceDiagnostics.start('save');
    const identity = context && {
      name: context.project.name,
      mainChipId: context.mainChip.id,
    };
    // Publish the pending promise before a synchronous capture can settle.
    await Promise.resolve();
    let succeeded = false;
    try {
      if (!context) throw Error('No project to save.');
      const capture = () => {
        if (this.context !== context || generation !== this.saveGeneration)
          throw Error('Obsolete project save');
        if (this.projectSaveBlockedReason) throw Error(this.projectSaveBlockedReason);
        if (!this.projectBaselineReady) {
          this.persistenceService.beginEditingProject(
            this.captureProjectForSave()!,
            null,
          );
          this.projectBaselineReady = true;
        }
        return {
          snapshot: this.captureProjectForSave()!,
          revision: this.manualEditRevision,
        };
      };
      // Both Save and autosave use the ordered immutable checkpoint. Neither
      // changes playback intent, nor promises pending-event continuation on reopen.
      const owner = authorityFor(context);
      const { snapshot, revision } = owner ? await owner.capture(capture) : capture();
      await this.persistProjectChanges(snapshot);
      succeeded = true;
      runInAction(() => {
        if (this.context !== context || generation !== this.saveGeneration) return;
        if (
          context.project.name !== identity!.name ||
          context.mainChip.id !== identity!.mainChipId
        )
          return;
        this.manualSaveDirty = revision !== this.manualEditRevision;
        this.manualSaveCompletedAt = Date.now();
      });
    } catch (error) {
      diagnostics.record('save');
      runInAction(() => {
        if (this.context !== context || generation !== this.saveGeneration) return;
        this.manualSaveDirty = true;
        if (identity && !this.projectSaveBlockedReason)
          this.updateProjectSaveError(
            identity.name,
            identity.mainChipId,
            projectStorageErrorMessage(error, 'Unable to save this project. Try again.'),
          );
      });
      throw error;
    } finally {
      performanceDiagnostics.finish('save', timing);
      runInAction(() => {
        if (generation !== this.saveGeneration) return;
        this.manualSaveInProgress = false;
        this.manualSavePromise = undefined;
        this.saveIsExplicit = false;
        // Retry late edits after success; failures wait for explicit retry/new edit.
        if (succeeded) this.scheduleProjectAutosave();
      });
    }
  }

  private captureProjectForSave(
    context = this.context,
  ): ProjectDto | CapturedProjectSnapshot | undefined {
    const project = context?.project;
    if (!context || !project) return;
    const capture = () => {
      if (!this.persistenceService.currentSnapshotsEnabled) return project.dto;
      try {
        if (!project.captureSnapshot)
          throw new Error('This project does not support current snapshot capture.');
        return project.captureSnapshot();
      } catch (error) {
        this.updateProjectSaveError(
          project.name,
          context.mainChip.id,
          error instanceof Error ? error.message : 'Unable to capture this project.',
        );
        throw error;
      }
    };
    let native = false;
    try {
      native = hasNativeProjectSnapshotHooks(context);
    } catch {
      /* Custom/opaque sources use the actual capture to establish dependencies. */
    }
    if (native) {
      this.customSnapshotReaction?.dispose();
      this.customSnapshotReaction = undefined;
      this.customSnapshotContext = undefined;
      this.customSnapshotInvalidated = false;
      return capture();
    }
    const generation = this.saveGeneration;
    if (this.customSnapshotContext !== context) {
      this.customSnapshotReaction?.dispose();
      this.customSnapshotReaction = undefined;
    }
    this.customSnapshotContext = context;
    const observer = (this.customSnapshotReaction ??= new Reaction(
      'Custom project snapshot inputs',
      () => {
        // Invalidating a snapshot only records an edit. Reading custom hooks again
        // waits for the same debounced, authoritative capture used by normal Save.
        if (this.saveGeneration !== generation) return;
        if (this.context === context) this.markProjectDirty();
        else if (this.customSnapshotContext === context)
          this.customSnapshotInvalidated = true;
      },
    ));
    let snapshot: ProjectDto | CapturedProjectSnapshot | undefined;
    let failed = false;
    let failure: unknown;
    this.customSnapshotInvalidated = false;
    observer.track(() => {
      try {
        snapshot = capture();
      } catch (error) {
        failed = true;
        failure = error;
      }
    });
    if (failed) {
      // Error recovery keeps the independent authored observer alive. Do not
      // re-run a failing serializer just to restore dependency tracking.
      observer.dispose();
      if (this.customSnapshotReaction === observer) {
        this.customSnapshotReaction = undefined;
        this.customSnapshotContext = undefined;
        this.customSnapshotInvalidated = false;
      }
      throw failure;
    }
    return snapshot;
  }

  private persistProjectChanges(
    dto: ProjectDto | CapturedProjectSnapshot,
  ): Promise<void> {
    const { name, mainChipId } = dto;
    // Persistence captures the input synchronously. Keep only identity fields
    // while awaiting completion, not this potentially project-sized DTO.
    try {
      const save =
        dto instanceof CapturedProjectSnapshot
          ? this.persistenceService.saveCurrentSnapshot(name, dto)
          : this.persistenceService.saveProjectChanges(name, dto);
      const completed = this.completeProjectSave(
        save,
        name,
        mainChipId,
        this.context,
        this.saveGeneration,
      );
      this.pendingProjectSaves.add(completed);
      completed.then(
        () => this.pendingProjectSaves.delete(completed),
        () => this.pendingProjectSaves.delete(completed),
      );
      return completed;
    } catch (error) {
      diagnostics.record('save');
      this.updateProjectSaveError(
        name,
        mainChipId,
        error instanceof Error ? error.message : 'Unable to save this project.',
      );
      return Promise.reject(error);
    }
  }

  private async completeProjectSave(
    save: Promise<void>,
    name: string,
    mainChipId: ProjectDto['mainChipId'],
    context: Context | undefined,
    generation: number,
  ): Promise<void> {
    try {
      await save;
      if (this.context !== context || this.saveGeneration !== generation) return;
      this.updateProjectSaveError(name, mainChipId, '');
    } catch (error) {
      diagnostics.record('save');
      if (this.context === context && this.saveGeneration === generation)
        this.updateProjectSaveError(
          name,
          mainChipId,
          error instanceof Error ? error.message : 'Unable to save this project.',
        );
      throw error;
    }
  }

  private updateProjectSaveError(
    name: string,
    mainChipId: ProjectDto['mainChipId'],
    message: string,
  ): void {
    runInAction(() => {
      if (this.context?.project.name === name && this.context?.mainChip.id === mainChipId)
        this.projectSaveError = message;
    });
  }

  async openChipInWindow(chipId: ChipId): Promise<void> {
    this.assertChallengeLibraryEditable();
    if (!this.context) return;
    const name = this.context.project.name;
    const newWindow = window.open('about:blank', '_blank');
    if (!newWindow) {
      this.projectSaveError = 'Allow popups to open this chip in another window.';
      return;
    }
    newWindow.opener = null;
    try {
      await this.saveCurrentProject();
      const url = new URL('/editor', window.location.href);
      url.search = new URLSearchParams({
        project: name,
        chip: chipId,
        circuit: CircuitType.ChipLogic,
      }).toString();
      newWindow.location.replace(url.href);
    } catch {
      newWindow.close();
    }
  }

  async loadSavedProject(
    name: string,
    signal?: AbortSignal,
    onProgress?: ProjectLoadObserver,
  ): Promise<Context> {
    const owner = authorityFor(this.context);
    if (owner)
      return owner.closeForProject(async () => {
        if (
          this.autosaveEnabled &&
          !this.manualSaveDeparting &&
          this.hasUnsavedProjectChanges
        )
          await this.saveCurrentProject();
        return this.loadSavedProjectNative(name, signal, onProgress);
      });
    return this.loadSavedProjectNative(name, signal, onProgress);
  }
  private async loadSavedProjectNative(
    name: string,
    signal?: AbortSignal,
    onProgress?: ProjectLoadObserver,
  ): Promise<Context> {
    const timing = performanceDiagnostics.start('load-storage');
    try {
      const generation = ++this.loadGeneration;
      const report = createProjectLoadReporter((progress) => {
        if (!signal?.aborted && generation === this.loadGeneration)
          onProgress?.(progress);
      });
      report('reading');
      const loaded =
        this.persistenceService.currentSnapshotsEnabled &&
        this.persistenceService.loadProjectForEditing
          ? await this.persistenceService.loadProjectForEditing(name, signal, report)
          : undefined;
      const projectDto =
        loaded === undefined
          ? await this.persistenceService.loadProject(name, signal, report)
          : loaded?.project;
      if (signal?.aborted || generation !== this.loadGeneration)
        throw new DOMException('Project load cancelled', 'AbortError');
      if (!projectDto) throw new ProjectNotFoundError(name);
      assertProjectDefinitionReferences(projectDto);
      this.teardown();
      const context = await this.loadProjectCooperatively(
        projectDto,
        signal,
        generation,
        loaded?.beginEditing,
        report,
      );
      this.openedStorageNames.set(context, name);
      return context;
    } finally {
      performanceDiagnostics.finish('load-storage', timing);
    }
  }

  async loadLastProject(
    signal?: AbortSignal,
    onProgress?: ProjectLoadObserver,
  ): Promise<Context | null> {
    const owner = authorityFor(this.context);
    if (owner)
      return owner.closeForProject(async () => {
        if (
          this.autosaveEnabled &&
          !this.manualSaveDeparting &&
          this.hasUnsavedProjectChanges
        )
          await this.saveCurrentProject();
        return this.loadLastProjectNative(signal, onProgress);
      });
    return this.loadLastProjectNative(signal, onProgress);
  }
  private async loadLastProjectNative(
    signal?: AbortSignal,
    onProgress?: ProjectLoadObserver,
  ): Promise<Context | null> {
    const timing = performanceDiagnostics.start('load-storage');
    try {
      const generation = ++this.loadGeneration;
      const report = createProjectLoadReporter((progress) => {
        if (!signal?.aborted && generation === this.loadGeneration)
          onProgress?.(progress);
      });
      report('reading');
      const lastProjectName = await this.persistenceService.getLastProjectName();
      if (signal?.aborted || generation !== this.loadGeneration)
        throw new DOMException('Project load cancelled', 'AbortError');
      if (!lastProjectName) return null;
      const loaded =
        this.persistenceService.currentSnapshotsEnabled &&
        this.persistenceService.loadProjectForEditing
          ? await this.persistenceService.loadProjectForEditing(
              lastProjectName,
              signal,
              report,
            )
          : undefined;
      const projectDto =
        loaded === undefined
          ? await this.persistenceService.loadProject(lastProjectName, signal, report)
          : loaded?.project;
      if (signal?.aborted || generation !== this.loadGeneration)
        throw new DOMException('Project load cancelled', 'AbortError');
      if (!projectDto) return null;
      assertProjectDefinitionReferences(projectDto);
      this.teardown();
      const context = await this.loadProjectCooperatively(
        projectDto,
        signal,
        generation,
        loaded?.beginEditing,
        report,
      );
      this.openedStorageNames.set(context, lastProjectName);
      return context;
    } finally {
      performanceDiagnostics.finish('load-storage', timing);
    }
  }

  /** Async entry points can retire themselves before a newer load replaces context. */
  get projectLoadGeneration(): number {
    return this.loadGeneration;
  }

  readSavedProjectIdentity(
    name: string,
    signal?: AbortSignal,
  ): Promise<string | undefined> {
    return (
      this.persistenceService.readProjectIdentity?.(name, signal) ??
      Promise.resolve(undefined)
    );
  }

  async listSavedProjects(): Promise<string[]> {
    return this.persistenceService.listProjects();
  }

  readSavedProjectLastOpened(names: readonly string[], signal?: AbortSignal) {
    return (
      this.persistenceService.readProjectLastOpened?.(names, signal) ??
      Promise.resolve({} as Record<string, number>)
    );
  }

  watchSavedProjectOpenHistory(onChange: () => void): () => void {
    return this.persistenceService.watchProjectOpenHistory?.(onChange) ?? (() => {});
  }

  /** Called only after this context has passed editor validation and drawn. */
  async recordProjectOpened(context: Context): Promise<void> {
    if (this.context !== context) return;
    await this.persistenceService.recordProjectOpened?.(
      this.openedStorageNames.get(context) ?? context.project.name,
      context.mainChip.id,
      Date.now(),
    );
  }

  async deleteSavedProject(name: string): Promise<void> {
    return this.persistenceService.deleteProject(name);
  }

  /** Save through the existing checkpoint before moving its storage key. The
   * live context, chip identities, undo history and playback remain intact. */
  async renameCurrentProject(newName: string): Promise<void> {
    const context = this.context;
    if (!context) throw Error('Open a project before renaming it.');
    if (
      this.projectRenameInProgress ||
      this.workerModeSwitchInProgress ||
      this.bundleIdentitySwitchInProgress
    )
      throw Error('Wait for the current project operation to finish.');
    const oldName = this.openedStorageNames.get(context) ?? context.project.name;
    const name = newName.trim();
    if (!name) throw Error('Enter a project name.');
    if (name === oldName && name === context.project.name) return;
    if (this.projectSaveBlockedReason) throw Error(this.projectSaveBlockedReason);
    this.projectRenameInProgress = true;
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.autoSaveTimer = undefined;
    try {
      const error = projectRenameError(name, oldName, await this.listSavedProjects());
      if (error) throw Error(error);
      if (this.context !== context) throw Error('The active project changed.');
      if (oldName === context.project.name) await this.queueProjectSave(true);
      else if (this.manualSavePromise) await this.manualSavePromise;
      if (this.context !== context) throw Error('The active project changed.');
      await this.persistenceService.renameProject(oldName, name);
      runInAction(() => {
        context.project.name = name;
        this.openedStorageNames.set(context, name);
      });
      // Finish late edits and acknowledge the new name in the same save session.
      await this.queueProjectSave(true);
    } finally {
      runInAction(() => {
        this.projectRenameInProgress = false;
      });
      this.scheduleProjectAutosave();
    }
  }

  async renameSavedProject(oldName: string, newName: string): Promise<void> {
    await this.persistenceService.renameProject(oldName, newName);
    if (this.context && this.openedStorageNames.get(this.context) === oldName)
      this.openedStorageNames.set(this.context, newName);
    if (this.context?.project && this.context.project.name === oldName) {
      this.context.project.name = newName;
    }
  }

  async copySavedProject(sourceName: string, newName: string): Promise<void> {
    await this.persistenceService.copyProject(sourceName, newName);
  }

  async exportSavedProject(
    name: string,
    signal?: AbortSignal,
  ): Promise<ProjectDto | null> {
    return this.persistenceService.loadProject(name, signal);
  }

  exportSavedProjectFile(name: string): Promise<Blob | null> {
    return this.persistenceService.exportProjectFile(name);
  }

  /** Capture every live graph once, including optional recovery drafts, before encoding yields. */
  async exportCurrentProjectFile(includeDrafts = false): Promise<Blob> {
    const project = this.context?.project;
    if (!project?.captureSnapshot)
      throw new Error('No current project is available to export.');
    const drafts = includeDrafts
      ? [...this.chipDrafts.values()].map(({ draft }) => draft)
      : [];
    const draftIds = new Set<string>(drafts.map((draft) => draft.id));
    const context = this.context;
    const capture = () => {
      if (this.context !== context) throw Error('Obsolete project export');
      return project.captureSnapshot!(drafts).project;
    };
    const captured = authorityFor(context)
      ? await authorityFor(context)!.capture(capture)
      : capture();
    return encodeProjectSnapshot({
      ...captured,
      chips: captured.chips.map((chip) =>
        draftIds.has(chip.id) ? { ...chip, name: `${chip.name} (unsaved draft)` } : chip,
      ),
    });
  }

  async importSharedProject(blob: Blob, signal?: AbortSignal): Promise<string> {
    return this.persistenceService.importSharedCopy(
      await readSharedProjectFile(blob, signal),
      signal,
    );
  }

  async importSavedProject(dto: ProjectDto): Promise<void> {
    await this.persistenceService.saveProject(dto.name, dto);
    await this.persistenceService.forgetProjectOpened?.(dto.name);
  }

  exportChip(chip: IChipModel): ChipExportBundle {
    const allDeps = chip.recursiveChildChips;
    const allChips = [...allDeps, chip];
    const chipDtos = allChips.map((c) => c.dto);
    const sorted = topologicalSortChipDtos(chipDtos);

    return {
      version: 1,
      exportedAt: new Date().toISOString(),
      rootChipId: chip.id,
      chips: sorted,
    };
  }

  exportChipFile(chip: IChipModel): Promise<Blob> {
    const capture = () => encodeCurrentChip(chip);
    const authority = authorityFor(this.context);
    return authority ? authority.capture(capture).then((blob) => blob) : capture();
  }

  importChipFile(bundle: ChipExportBundle): IChipModel[] {
    if (this.context?.challenge && !this.context.challengeAuthoring)
      throw Error('Chip importing is disabled during challenge play.');
    if (!this.context) throw new Error('Create a project before importing chips.');
    const timing = performanceDiagnostics.start('import-apply');
    try {
      return importChipFileBundle(this.context, this.chipModelFactory, bundle);
    } catch (error) {
      diagnostics.record('import');
      throw error;
    } finally {
      performanceDiagnostics.finish('import-apply', timing);
    }
  }

  importChips(bundle: ChipExportBundle): IChipModel[] {
    if (this.context?.challenge && !this.context.challengeAuthoring)
      throw Error('Chip importing is disabled during challenge play.');
    if (!this.context) {
      throw new Error('Context not initialized. Create a project first.');
    }

    const validated = this.persistenceService.validateChipExportBundleEnvelope(bundle);
    const sorted = topologicalSortChipDtos(validated.chips);

    const imported: IChipModel[] = [];

    for (const chipDto of sorted) {
      if (this.chipStoreModel.getChip(chipDto.id as ChipId)) {
        continue;
      }
      const chip = this.chipModelFactory.fromDto(this.context, chipDto);
      this.chipStoreModel.addChip(chip);
      imported.push(chip);
    }

    // Mirror the post-load sequence from loadProject():
    // 1. Rebuild template graphs and refresh instances in topological order
    for (const chip of imported) {
      chip.rebuildTemplateGraph();
      for (const otherChip of this.chipStoreModel.chips) {
        for (const chipInstance of otherChip.logicCircuit.chipInstances.values()) {
          if (chipInstance.chipId === chip.id) {
            chipInstance.refreshGraph();
          }
        }
      }
    }

    // 2. Pre-seed each imported chip's GraphManager with editing graph
    //    so rebuildGraph() can save/restore node state
    for (const chip of imported) {
      const editingGraph = chip.editingInstance.logicGraph;
      if (editingGraph && editingGraph.nodes.size > 0) {
        chip.logicCircuit.graphManager.graph = editingGraph as LogicGraph;
      }
    }

    // 3. Rebuild editing logic graphs so simulation works
    for (const chip of imported) {
      chip.logicCircuit.rebuildGraph();
      this.recoverRestoredLatches(chip);
    }

    return imported;
  }

  private recoverRestoredLatches(chip: IChipModel) {
    const logic = chip.logicCircuit;
    if (!recoverSavedLatches(logic.logicGraph)) return;
    // Recheck downstream nodes and refresh rendered tiles after the repair.
    logic.logicSimulation.setGraph(logic.logicGraph);
    logic.logicSimulation.drainUntilStable(false);
    logic.logicSimulation.onPostUpdate?.(new Set(logic.logicGraph.nodes.keys()));
  }

  getChipDependencyInfo(chipId: ChipId): ChipDependencyInfo {
    const dependants: IChipModel[] = [];
    let totalInstances = 0;

    for (const chip of this.chipStoreModel.chips) {
      let countInChip = 0;
      for (const instance of chip.logicCircuit.chipInstances.values()) {
        if (instance.chipId === chipId) {
          countInChip++;
        }
      }
      if (countInChip > 0) {
        dependants.push(chip);
        totalInstances += countInChip;
      }
    }

    return { dependants, totalInstances };
  }

  deleteChipWithInstances(chipId: ChipId) {
    this.assertChallengeLibraryEditable(this.chipStoreModel.getChip(chipId));
    if (!this.context) {
      throw new Error('Context not initialized');
    }

    if (this.chipStoreModel.chips.length <= 1) {
      throw new Error('Cannot delete the last chip');
    }

    const templateOwners = [
      ...this.chipStoreModel.chips,
      ...[...this.chipDrafts.values()].map((entry) => entry.draft),
    ].filter(
      (chip) =>
        chip.templateType &&
        [...chip.logicCircuit.chipInstances.values()].some(
          (instance) => instance.chipId === chipId,
        ),
    );
    if (templateOwners.length)
      throw new Error(
        `Used by template: ${templateOwners[0].name}. Remove it from the template before deleting this chip.`,
      );
    if (this.context.editor.pendingTemplateId === chipId)
      this.context.editor.cancelPaste();
    for (const [id, entry] of this.chipDrafts)
      if (entry.source.id === chipId) this.discardChipDraft(id);
    const { dependants } = this.getChipDependencyInfo(chipId);

    for (const parentChip of dependants) {
      const chipEditor = this.chipStoreModel.getChipEditor(parentChip.id);
      if (!chipEditor) continue;

      const instancesToRemove = Array.from(
        parentChip.logicCircuit.chipInstances.values(),
      ).filter((inst) => inst.chipId === chipId);

      for (const instance of instancesToRemove) {
        const circuitModel = parentChip.logicCircuit.circuitModel;
        const tileManager = circuitModel.tileManagerModel;
        const connectionModel = circuitModel.connectionModel;

        const tilesToRemove = tileManager.tiles.filter(
          (tile) => tile.meta.partOfChip === instance.id,
        );
        for (const tile of tilesToRemove) {
          connectionModel.removeConnectionsForTile(tile.id);
          tileManager.remove(tile);
        }

        const chipIdx = circuitModel.chips.indexOf(instance);
        if (chipIdx >= 0) {
          circuitModel.chips.splice(chipIdx, 1);
        }
        parentChip.logicCircuit.chipInstances.delete(instance.id);
        this.context.project?.chipInstanceMap?.delete(instance.id);
      }

      // Clear undo history since we modified state outside the history system
      chipEditor.logicCircuitEditor.historyModel.previousTransactions = [];
      chipEditor.logicCircuitEditor.historyModel.futureTransactions = [];

      if (!parentChip.deferUnusedTemplate) parentChip.rebuildTemplateGraph();
      parentChip.logicCircuit.rebuildGraph();
    }

    // If currently editing the deleted chip, navigate away
    if (this.context.editor.currentChip.id === chipId) {
      const remaining = this.chipStoreModel.chips.find((c) => c.id !== chipId);
      if (remaining) {
        this.navigateToChip(remaining);
      }
    }

    // Clear place chip selection if it references the deleted chip
    if (this.context.editor.placeChipModel.selectedChip?.id === chipId) {
      this.context.editor.placeChipModel.selectedChip = null;
      this.context.editor.setEditingMode(EditingMode.SELECT);
    }

    this.chipStoreModel.deleteChip(chipId);
  }

  private teardown() {
    const owner = authorityFor(this.context);
    if (owner && owner.ownsRuntime)
      throw Error('Pause and release worker before replacing the project');
    this.saveGeneration++;
    this.manualSavePromise = undefined;
    this.explicitSavePromise = undefined;
    this.manualSaveInProgress = false;
    owner?.dispose();
    this.autoSaveDisposer?.();
    this.manualDirtyDisposer?.();
    this.manualDirtyDisposer = undefined;
    this.customSnapshotReaction?.dispose();
    this.customSnapshotReaction = undefined;
    this.customSnapshotContext = undefined;
    this.customSnapshotInvalidated = false;
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer);
    this.clearChipStore();
    this.context?.runtimeGraphIds?.close();
    this.context = undefined;
  }

  private createBaseContext(): Context {
    const baseContext: Partial<Context> = {
      // The public manual-save experiment deliberately uses ordinary native
      // reconstruction. Exact continuation remains a lab-only capability.
      nativeRuntimeCheckpoint: !this.manualSaveEnabled && nativeCheckpointOptIn(),
      runtimeGraphIds: new RuntimeGraphIds(),
      app: this,
      chipStore: this.chipStoreModel,
      eventBus: this.eventBusFactory.create(),
      keyMap: this.userPreferencesModel.keyMapModel,
      pixiManager: this.pixiManagerModel,
      userPreferences: this.userPreferencesModel,
      ui: this.uiModel,
      viewStateStore: new ViewStateStore(),
    };

    // Authority maps, tiles, sessions and AppModel must share one identity.
    // Wrapping only when publishing AppModel.context creates a second proxy.
    return observable.object(baseContext, {}, { deep: false }) as Context;
  }

  private clearChipStore() {
    for (const id of [...this.chipDrafts.keys()]) this.discardChipDraft(id);
    for (const chip of this.chipStoreModel.chips) {
      this.chipStoreModel.deleteChip(chip.id);
    }
  }
}

export const AppModelContext = React.createContext<AppModel | null>(null);

export const useAppModel = (): AppModel => {
  const appModel = React.useContext(AppModelContext);

  if (!appModel) {
    throw new Error('useAppModel must be used within AppModelProvider');
  }

  return appModel;
};

/** Project-scoped components require an initialized project context. */
export const useProjectContext = (): Context => {
  const { context } = useAppModel();
  if (!context) {
    throw new Error('useProjectContext requires an initialized project');
  }
  return context;
};
